import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    CheckIcon,
    ClockIcon,
    EllipsisVerticalIcon,
    EnvelopeIcon,
    GlobeAltIcon,
    MagnifyingGlassIcon,
    RectangleGroupIcon,
    Squares2X2Icon,
    TrashIcon,
    UserGroupIcon,
    UserIcon,
} from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import { GetAllUsersResponse, GetAllUsersResponseAuthSpecificOneOf1 } from "src/api/generated/frontend_internal_v1";
import WS from "src/api/ws";
import ExpireUserDevices from "src/components/dialogs/expire-user-devices";
import AddToGroupDialog from "src/components/dialogs/add-to-group";
import { useForm } from "@tanstack/react-form";
import { Input, InputGroup } from "src/components/base/input";
import HeadingLayout from "src/components/base/heading-layout";
import { PrimaryButton } from "src/components/base/button";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import DeleteLocalUserDialog from "src/components/dialogs/delete-local-user";
import { EmptyState } from "src/components/base/empty-state";
import { UsersIcon } from "@heroicons/react/24/solid";
import { DataTable, useDataTable } from "src/components/data-table";
import { ToggleButton } from "src/components/toggle-button";
import HIGHLIGHT_CONTEXT from "src/context/highlight";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { TFunction } from "i18next";
import { TableHeader } from "src/components/base/table";
import { TenantBadge } from "src/components/badges/tenant";
import { FilterTile, FilterTileGroup } from "src/components/base/filter-tile";
import { Strong, Text } from "src/components/base/text";
import { HighlightTerm } from "src/components/highlight-term";
import { Badge } from "src/components/base/badge";
import { XMarkIcon } from "@heroicons/react/16/solid";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";

/**
 * The properties for {@link UserOverview}
 */
export type UserOverviewProps = {};

/**
 * Filter options
 */
const FILTERS = ["all", "local-only", "oidc-only", "invited-only"] as const;

/**
 * One of the {@link FILTERS} options
 */
type Filter = (typeof FILTERS)[number];

/**
 * Checks if the access is expired for an invited user
 *
 * @param auth auth specific of the user
 * @returns true if the access is expired, false otherwise
 */
function isExpiredInvited(auth: GetAllUsersResponseAuthSpecificOneOf1): boolean {
    const expiresAt = auth.access_expires_at ? new Date(auth.access_expires_at) : null;
    return !!expiresAt && expiresAt < new Date();
}

/**
 * Builds the access-period text shown for local and invited users.
 *
 * @param auth auth specific of a local or invited user
 * @param t for Translation
 * @returns the formatted period, or null when there is nothing to show
 */
function accessPeriodText(
    auth: Pick<GetAllUsersResponseAuthSpecificOneOf1, "is_access_planned" | "access_start" | "access_expires_at">,
    t: TFunction,
): string | null {
    const start = auth.access_start ? new Date(auth.access_start).toLocaleDateString("de-DE") : null;
    const end = auth.access_expires_at ? new Date(auth.access_expires_at).toLocaleDateString("de-DE") : null;

    if (auth.is_access_planned && start) {
        return end ? t("label.access-planned-from-until", { start, end }) : t("label.access-planned-from", { start });
    }

    return end ? t("label.access-valid-until", { end }) : null;
}

/**
 * Overview over all user accounts
 */
function UserOverview(props: UserOverviewProps) {
    const [t] = useTranslation("admin-user-overview");
    const [tg] = useTranslation();

    const router = useRouter();
    const navigate = Route.useNavigate();

    const { filter, search, tenant, page } = Route.useSearch();
    const { users, tenants } = Route.useLoaderData();

    // Popups
    const [expireUser, setExpireUser] = React.useState<GetAllUsersResponse>();
    const [addToGroup, setAddToGroup] = React.useState<GetAllUsersResponse>();
    const [deleteUser, setDeleteUser] = React.useState<GetAllUsersResponse>();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.UsersChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    const columns = useUserColumns({
        setAddToGroup,
        setExpireUser,
        setDeleteUser,
    });
    const { table, isGrouped, setGrouping } = useDataTable({
        data: users.list,
        columns,
        initialSorting: [{ id: "name", desc: false }],
        groupBy: ["tenant"],
        pageIndex: page - 1,
        onPageChange: (pageIndex) => navigate({ search: (prev) => ({ ...prev, page: pageIndex + 1 }), replace: true }),
    });

    // Filtering is fully client-side: the `search` and `filter` URL params are
    // the source of truth, and these effects push them into the table. The
    // loader has no `loaderDeps`, so updating them never re-fetches the data.
    // Page resets happen at the interaction sites (navigating with `page: 1`)
    // instead of here, so a deep-linked page isn't wiped on mount.
    const setAuthFilter = (f: Filter) =>
        navigate({ search: (prev) => ({ ...prev, filter: f, page: 1 }), replace: true });

    useEffect(() => {
        table.getColumn("tenant")?.setFilterValue(tenant === "all" ? undefined : tenant);
    }, [tenant]);

    useEffect(() => {
        table
            .getColumn("user-auth")
            ?.setFilterValue(
                filter === "local-only"
                    ? "Local"
                    : filter === "oidc-only"
                      ? "Remote"
                      : filter === "invited-only"
                        ? "Invited"
                        : undefined,
            );
    }, [filter]);

    useEffect(() => {
        table.setGlobalFilter(search);
    }, [search]);

    const tenantItems = tenant === "all" ? users.list : users.list.filter((u) => u.tenant.uuid === tenant);
    const chipTotal = tenantItems.length;
    const chipLocal = tenantItems.filter((u) => u.auth === "Local").length;
    const chipOidc = tenantItems.filter((u) => u.auth === "Remote").length;
    const chipInvited = tenantItems.filter((u) => u.auth === "Invited").length;

    return (
        <HeadingLayout
            heading={t("heading.user-overview")}
            headingChildren={<PrimaryButton href={"/a/users/invite"}>{t("button.create-invitation")}</PrimaryButton>}
        >
            {/* Stat strip — gives a one-glance summary that survives filtering. */}
            <FilterTileGroup
                value={filter}
                onChange={setAuthFilter}
                aria-label={t("label.filter")}
                className={"grid grid-cols-2 gap-3 sm:grid-cols-4"}
            >
                <FilterTile
                    value={"all"}
                    icon={<Squares2X2Icon className={"size-5"} />}
                    label={t("label.total-users")}
                    count={chipTotal}
                />
                <FilterTile
                    value={"local-only"}
                    icon={<UserIcon className={"size-5"} />}
                    label={t("label.local")}
                    count={chipLocal}
                />
                <FilterTile
                    value={"oidc-only"}
                    icon={<GlobeAltIcon className={"size-5"} />}
                    label={t("label.oidc")}
                    count={chipOidc}
                />
                <FilterTile
                    value={"invited-only"}
                    icon={<EnvelopeIcon className={"size-5"} />}
                    label={t("label.invite")}
                    count={chipInvited}
                />
            </FilterTileGroup>

            <div className={"flex flex-col gap-3 sm:flex-row sm:items-center"}>
                <div className={"flex-1"}>
                    <form.Field
                        name={"search"}
                        validators={{
                            onChangeAsync: async ({ fieldApi }) => {
                                await navigate({
                                    search: (prev) => ({ ...prev, search: fieldApi.state.value, page: 1 }),
                                    replace: true,
                                });
                            },
                            onChangeAsyncDebounceMs: 500,
                        }}
                    >
                        {(fieldApi) => (
                            <InputGroup>
                                <MagnifyingGlassIcon data-slot="icon" />
                                <Input
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    placeholder={t("label.filter")}
                                    aria-label={t("label.filter")}
                                />
                            </InputGroup>
                        )}
                    </form.Field>
                </div>
                <div className={"sm:w-56"}>
                    <Listbox
                        aria-label={t("label.filter-by-tenant")}
                        value={tenant}
                        onChange={async (tenant_uuid) =>
                            await navigate({
                                search: (prev) => ({ ...prev, tenant: tenant_uuid, page: 1 }),
                                replace: true,
                            })
                        }
                    >
                        <ListboxOption value={"all"}>
                            <ListboxLabel>{t("label.all-tenants")}</ListboxLabel>
                        </ListboxOption>
                        {tenants.list.map((tenant) => (
                            <ListboxOption key={tenant.uuid} value={tenant.uuid}>
                                <ListboxLabel>{tenant.name}</ListboxLabel>
                            </ListboxOption>
                        ))}
                    </Listbox>
                </div>
                <ToggleButton active={isGrouped} onClick={() => setGrouping(!isGrouped)}>
                    <RectangleGroupIcon />
                    {t("label.group-by-tenant")}
                </ToggleButton>
            </div>

            {users.list.length === 0 ? (
                <EmptyState
                    icon={<UsersIcon />}
                    title={tg("empty.no-results-title")}
                    description={tg("empty.no-results-description")}
                    action={<PrimaryButton href={"/a/users/invite"}>{t("button.create-invitation")}</PrimaryButton>}
                />
            ) : (
                <HIGHLIGHT_CONTEXT.Provider value={{ searchTerm: table.getState().globalFilter }}>
                    <DataTable
                        table={table}
                        groupBy={isGrouped ? ["tenant"] : undefined}
                        rowHref={"/a/users/$userId/general"}
                        rowParams={(x) => ({ userId: x.uuid })}
                    />
                </HIGHLIGHT_CONTEXT.Provider>
            )}

            <Suspense>
                <ExpireUserDevices
                    onClose={() => setExpireUser(undefined)}
                    onExpire={() => setExpireUser(undefined)}
                    isOpen={!!expireUser}
                    user={expireUser}
                />

                <AddToGroupDialog
                    isOpen={!!addToGroup}
                    onClose={() => setAddToGroup(undefined)}
                    onSubmit={() => setAddToGroup(undefined)}
                    user={addToGroup}
                />

                <DeleteLocalUserDialog
                    simpleUser={deleteUser}
                    isOpen={!!deleteUser}
                    onClose={() => setDeleteUser(undefined)}
                    onDelete={() => setDeleteUser(undefined)}
                />
            </Suspense>
        </HeadingLayout>
    );
}

/**
 * The properties for {@link useUserColumns}
 */
export type UserColumnsProps = {
    /** Open the add-to-group popup */
    setAddToGroup: (user: GetAllUsersResponse) => void;

    /** Open the expire-devices popup */
    setExpireUser: (user: GetAllUsersResponse) => void;

    /** Open the delete user popup */
    setDeleteUser: (user: GetAllUsersResponse) => void;
};

/**
 * The columns in the user overview table
 */
export function useUserColumns(props: UserColumnsProps): ColumnDef<GetAllUsersResponse, string>[] {
    const [t] = useTranslation("admin-user-overview");
    const [tg] = useTranslation();

    const { setAddToGroup, setExpireUser, setDeleteUser } = props;

    const columnHelper = createColumnHelper<GetAllUsersResponse>();

    return [
        columnHelper.accessor((user) => user.tenant.name, {
            id: "tenant",
            filterFn: (row, _columnId, filterValue: string) => row.original.tenant.uuid === filterValue,
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.tenant")}
                </TableHeader>
            ),
            cell: (info) => <TenantBadge tenant={info.row.original.tenant} />,
        }),

        columnHelper.accessor((user) => user.display_name, {
            id: "name",
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.name")}
                </TableHeader>
            ),
            cell: (info) => (
                <Strong>
                    <HighlightTerm value={info.row.original.display_name} />
                </Strong>
            ),
        }),

        columnHelper.accessor((user) => (user.auth_specific.type === "Remote" ? user.auth_specific.remote_id : ""), {
            id: "external-id",
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.external-id")}
                </TableHeader>
            ),
            cell: (info) => <HighlightTerm value={info.getValue()} />,
            meta: {
                className: "max-lg:hidden",
            },
        }),

        columnHelper.accessor((user) => user.auth, {
            id: "user-auth",
            enableGlobalFilter: false,
            filterFn: (row, _columnId, filterValue: string) => row.original.auth === filterValue,
            header: ({ column }) => (
                <TableHeader
                    sortable
                    direction={column.getIsSorted() || undefined}
                    onSort={column.getToggleSortingHandler()}
                >
                    {t("label.user-auth")}
                </TableHeader>
            ),
            cell: (info) => {
                const user = info.row.original;
                return user.auth === "Remote" ? (
                    <Badge color={"zinc"}>{t("label.oidc")}</Badge>
                ) : user.auth === "Invited" ? (
                    <Badge color={"blue"}>{t("label.invite")}</Badge>
                ) : user.auth === "Local" ? (
                    <Badge color={"zinc"}>{t("label.local")}</Badge>
                ) : (
                    <Badge color={"zinc"}>{t("label.unknow")}</Badge>
                );
            },
        }),

        columnHelper.accessor(
            (user) => (user.auth_specific.type === "Remote" ? "" : (user.auth_specific.access_expires_at ?? "")),
            {
                id: "access-period",
                enableGlobalFilter: false,
                header: ({ column }) => (
                    <TableHeader
                        sortable
                        direction={column.getIsSorted() || undefined}
                        onSort={column.getToggleSortingHandler()}
                    >
                        {t("label.access-period")}
                    </TableHeader>
                ),
                cell: (info) => {
                    const user = info.row.original;
                    return user.auth_specific.type === "Remote" ? (
                        <Text>-</Text>
                    ) : user.auth_specific.type === "Local" ? (
                        <div className={"flex gap-2"}>
                            <Badge
                                color={
                                    user.auth_specific.has_access
                                        ? "green"
                                        : user.auth_specific.is_access_planned
                                          ? "blue"
                                          : "zinc"
                                }
                            >
                                {user.auth_specific.has_access ? (
                                    <>
                                        <CheckIcon className={"size-4"} />
                                        {t("label.access-valid")}
                                    </>
                                ) : user.auth_specific.is_access_planned ? (
                                    <>
                                        <ClockIcon className={"size-4"} />
                                        {t("label.access-planned")}
                                    </>
                                ) : (
                                    <>
                                        <XMarkIcon className={"size-4"} />
                                        {t("label.access-expired")}
                                    </>
                                )}
                            </Badge>
                            {accessPeriodText(user.auth_specific, t) && (
                                <Text>{accessPeriodText(user.auth_specific, t)}</Text>
                            )}
                        </div>
                    ) : user.auth_specific.type === "Invited" ? (
                        <div className={"flex gap-2"}>
                            <Badge
                                color={
                                    isExpiredInvited(user.auth_specific)
                                        ? "zinc"
                                        : user.auth_specific.is_access_planned
                                          ? "blue"
                                          : "green"
                                }
                            >
                                {isExpiredInvited(user.auth_specific) ? (
                                    <>
                                        <XMarkIcon className={"size-4"} />
                                        {t("label.access-expired")}
                                    </>
                                ) : user.auth_specific.is_access_planned ? (
                                    <>
                                        <ClockIcon className={"size-4"} />
                                        {t("label.access-planned")}
                                    </>
                                ) : (
                                    <>
                                        <CheckIcon className={"size-4"} />
                                        {t("label.access-valid")}
                                    </>
                                )}
                            </Badge>
                            {accessPeriodText(user.auth_specific, t) && (
                                <Text>{accessPeriodText(user.auth_specific, t)}</Text>
                            )}
                        </div>
                    ) : (
                        <Text>-</Text>
                    );
                },
            },
        ),

        columnHelper.display({
            id: "actions",
            header: () => (
                <TableHeader className={"w-0"}>
                    <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                </TableHeader>
            ),
            size: 0,
            cell: ({ row }) => (
                <Dropdown>
                    <DropdownButton plain aria-label={tg("accessibility.actions")}>
                        <EllipsisVerticalIcon />
                    </DropdownButton>
                    <DropdownMenu anchor={"bottom end"}>
                        <DropdownSection>
                            <DropdownItem onClick={() => setAddToGroup(row.original)}>
                                <UserGroupIcon />
                                <DropdownLabel>{t("button.add-to-group")}</DropdownLabel>
                            </DropdownItem>
                        </DropdownSection>

                        <DropdownDivider />

                        <DropdownSection>
                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>

                            <DropdownItem onClick={() => setExpireUser(row.original)}>
                                <ClockIcon />
                                <DropdownLabel>{t("button.expire-user")}</DropdownLabel>
                            </DropdownItem>

                            {(row.original.auth === "Local" || row.original.auth === "Invited") && (
                                <DropdownItem onClick={() => setDeleteUser(row.original)}>
                                    <TrashIcon />
                                    <DropdownLabel>{t("button.delete-user")}</DropdownLabel>
                                </DropdownItem>
                            )}
                        </DropdownSection>
                    </DropdownMenu>
                </Dropdown>
            ),
            enableHiding: false,
        }),
    ];
}

/** The search parameter of the users view */
type UserOverviewSearch = {
    /** The search term that should be used */
    search: string;
    /** The filter that should be used */
    filter: Filter;
    /** The tenant that should be used */
    tenant: "all" | string;
    /** The page to display */
    page: number;
};

export const Route = createFileRoute("/_admin/a/users/")({
    component: UserOverview,
    validateSearch: (search: Record<string, unknown>): UserOverviewSearch => {
        const page = Number(search.page ?? 1);
        return {
            search: typeof search.search === "string" ? search.search : "",
            filter: FILTERS.includes(search.filter as Filter) ? (search.filter as Filter) : "all",
            tenant: typeof search.tenant === "string" ? search.tenant : "all",
            page: Number.isInteger(page) && page > 0 ? page : 1,
        };
    },
    loader: async () => {
        const [users, tenants] = await Promise.all([ApiInternal.users.getAll(), ApiInternal.tenants.getAll()]);
        return { users, tenants };
    },
});
